/**
 * A setting the user picks once and the browser remembers.
 *
 * localStorage, not the server. The choice this was written for — whether a
 * policy opens on its own or beside the list — is about the shape of the
 * window in front of you, so it belongs to the machine you are sitting at
 * rather than to your account. A nurse at a cart and the same nurse at the
 * station want different answers, and neither wants the other's.
 *
 * THE TRADE-OFF, stated plainly: on a shared workstation the next person
 * inherits the last person's choice. That is the right cost for a layout
 * preference and would be the wrong cost for anything that touched what a
 * person is allowed to see. Nothing here decides access.
 *
 * ONE STORE, NOT ONE PER CALLER. The first version of this kept the value in
 * useState, which meant two components reading the same setting held two
 * unrelated copies: the control on the list page wrote 'split' to storage and
 * the frame, holding its own stale copy, carried on as though nothing had
 * happened. The setting only appeared to work after a reload. A module-level
 * store with useSyncExternalStore gives every caller in the tab the same value
 * and the same notification.
 *
 * EVERY ACCESS IS GUARDED. localStorage throws rather than returning null in a
 * private window, under an enterprise storage policy, or when a site's data has
 * been blocked — and a hospital laptop is exactly where those settings get
 * turned on. A preference that cannot be saved must degrade to the default,
 * never to a blank screen.
 */

import { useCallback, useSyncExternalStore } from 'react';

const PREFIX = 'somexai.';

/**
 * Last known value per key.
 *
 * Also what makes `getSnapshot` safe: useSyncExternalStore re-renders forever
 * if the snapshot is not referentially stable, so reads come from here rather
 * than from storage on every render.
 */
const cache = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();

function readThrough(key: string): string | null {
  if (cache.has(key)) return cache.get(key) ?? null;
  let value: string | null = null;
  try {
    value = window.localStorage.getItem(PREFIX + key);
  } catch {
    // Storage unavailable; the default applies.
  }
  cache.set(key, value);
  return value;
}

function notify(key: string): void {
  listeners.get(key)?.forEach((fn) => fn());
}

function subscribe(key: string, onChange: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(onChange);

  // One window listener per key, attached with the first subscriber. The
  // `storage` event fires only in OTHER tabs, so this cannot loop back.
  const onStorage = (e: StorageEvent) => {
    if (e.key !== PREFIX + key) return;
    cache.set(key, e.newValue);
    notify(key);
  };
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener('storage', onStorage);
    set.delete(onChange);
    if (set.size === 0) listeners.delete(key);
  };
}

/** Exposed for tests, which need a clean slate between cases. */
export function resetPersistentChoices(): void {
  cache.clear();
}

/**
 * A choice from a fixed set, remembered between sessions.
 *
 * `allowed` is the whole point of the signature: whatever comes back from
 * storage was last written by some earlier version of this app, or by a person
 * with devtools open, so it is validated against what the code supports today
 * rather than trusted. An unrecognised value falls back to the default.
 */
export function usePersistentChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const raw = useSyncExternalStore(
    useCallback((onChange: () => void) => subscribe(key, onChange), [key]),
    useCallback(() => readThrough(key), [key]),
    // No server rendering here, but the third argument is required and the
    // honest answer is "nothing has been chosen".
    () => null,
  );

  const choice = raw !== null && allowed.includes(raw as T) ? (raw as T) : fallback;

  const set = useCallback(
    (next: T) => {
      cache.set(key, next);
      try {
        window.localStorage.setItem(PREFIX + key, next);
      } catch {
        // The choice still applies for this session; it just will not outlive
        // the tab.
      }
      notify(key);
    },
    [key],
  );

  return [choice, set];
}
